/**
 * Driver Web Push helpers — registers /driver-sw.js and subscribes with the
 * backend VAPID public key. Falls back silently when unsupported or denied.
 */
import { API } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerDriverServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/driver-sw.js", { scope: "/driver" });
    return reg;
  } catch (e) {
    console.warn("SW register failed", e);
    return null;
  }
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function requestPushPermission() {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
}

export async function subscribeDriverToPush(auth) {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const permission = await requestPushPermission();
  if (permission !== "granted") return { ok: false, reason: permission };

  const reg = await navigator.serviceWorker.ready;

  // Fetch VAPID public key
  const keyRes = await fetch(`${API}/nemt/driver/push/vapid-key`);
  if (!keyRes.ok) return { ok: false, reason: "no_vapid_key" };
  const { public_key: publicKey } = await keyRes.json();
  if (!publicKey) return { ok: false, reason: "no_vapid_key" };

  // Get / create subscription
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  const body = {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  };
  const res = await fetch(`${API}/nemt/driver/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Driver-Auth": auth },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, reason: "subscribe_failed" };
  return { ok: true };
}

/** WebAudio-based alarm — used as fallback when push is denied.
 * Two-tone chirp, 4x, ~2s total. Requires a prior user gesture on iOS.
 */
export function playAlarm() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const beep = (start, freq) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.22);
      osc.connect(g).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.25);
    };
    for (let i = 0; i < 4; i++) {
      beep(now + i * 0.5, 880);
      beep(now + i * 0.5 + 0.25, 660);
    }
    setTimeout(() => ctx.close().catch(() => {}), 2500);
  } catch (e) {
    /* silent */
  }
  try {
    if (navigator.vibrate) navigator.vibrate([400, 200, 400, 200, 400]);
  } catch (e) { /* silent */ }
}
